import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { LogOut, ChevronLeft, ChevronRight, UserCog } from 'lucide-react';
import clsx from 'clsx';
import { useAuth } from '../../context/AuthContext';
import { MyProfileModal } from '../profile/MyProfileModal';
import { NAV_ROUTES } from '../../utils/constants';
import { NAV_ITEMS } from './navItems';

interface SidebarProps {
  criticalCount?: number;
}

export function Sidebar({ criticalCount = 0 }: SidebarProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const visibleItems = NAV_ITEMS.filter(item =>
    user ? item.roles.includes(user.role) && (!item.feature || user.features[item.feature]) : false
  );

  const handleLogout = () => { logout(); navigate(NAV_ROUTES.LOGIN); };

  return (
    <>
      <aside className={clsx('sidebar', collapsed && 'sidebar--collapsed')}>
        {/* Logo */}
        <div className="sidebar__logo">
          {collapsed ? (
            <div className="sidebar__logo-icon-only">
              <img src="/assets/paitron_logo.png" alt="Paitron" className="sidebar__logo-img--mini"
                onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
            </div>
          ) : (
            <img src="/assets/paitron_logo.png" alt="Paitron Radar" className="sidebar__logo-img"
              onError={e => {
                const img = e.target as HTMLImageElement;
                img.style.display = 'none';
                const fallback = img.nextElementSibling as HTMLElement;
                if (fallback) fallback.style.display = 'flex';
              }}
            />
          )}
          {!collapsed && (
            <div className="sidebar__logo-fallback" style={{ display: 'none' }}>
              <span className="sidebar__logo-name">Paitron</span>
              <span className="sidebar__logo-sub">Radar</span>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav className="sidebar__nav">
          {visibleItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                clsx('sidebar__nav-item', isActive && 'sidebar__nav-item--active')
              }
              title={collapsed ? item.label : undefined}
            >
              <span className="sidebar__nav-icon">{item.icon}</span>
              {!collapsed && <span className="sidebar__nav-label">{item.label}</span>}
              {item.to === NAV_ROUTES.ALERTS && criticalCount > 0 && (
                <span className={clsx('sidebar__badge', collapsed && 'sidebar__badge--mini')}>
                  {criticalCount}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Footer */}
        <div className="sidebar__footer">
          {user && (
            <button
              className="sidebar__user sidebar__user--clickable"
              onClick={() => setProfileOpen(true)}
              title="Ver y editar mi perfil"
              id="sidebar-user-profile-btn"
            >
              <div className="sidebar__user-avatar">
                {user.name.charAt(0).toUpperCase()}
              </div>
              {!collapsed && (
                <>
                  <div className="sidebar__user-info">
                    <p className="sidebar__user-name">{user.name}</p>
                    <p className="sidebar__user-role">{user.role}</p>
                  </div>
                  <UserCog size={15} className="sidebar__user-edit-icon" />
                </>
              )}
            </button>
          )}
          <button className="sidebar__nav-item sidebar__logout" onClick={handleLogout} title="Cerrar sesión">
            <LogOut size={18} />
            {!collapsed && <span>Cerrar sesión</span>}
          </button>
          <button
            className="sidebar__collapse-btn"
            onClick={() => setCollapsed(c => !c)}
            aria-label={collapsed ? 'Expandir menú' : 'Colapsar menú'}
          >
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        </div>
      </aside>

      <MyProfileModal isOpen={profileOpen} onClose={() => setProfileOpen(false)} />
    </>
  );
}
